import Link from 'next/link';

import agents from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  CheckGlyph,
  delay,
  Stage,
  useOpenAgentDemo,
} from '@/components/agents/primitives';

import {
  BRAND_BG,
  BUSINESS,
  PhoneGlyph,
  SIGNUP_HREF,
  useReceptionistT,
  WAVE,
} from './kit';

export default function ReceptionistFinalCta() {
  const { t } = useReceptionistT();
  const openDemo = useOpenAgentDemo();

  return (
    <section className='mx-auto max-w-7xl px-3 pb-24 pt-8 sm:px-6 sm:pb-32 lg:px-8'>
      <Stage
        variant='indigo'
        halftoneAt='100% 100%'
        className='px-6 py-16 sm:px-14 sm:py-24'
      >
        <div className='relative grid items-center gap-14 lg:grid-cols-[1.7fr_1fr]'>
          <div>
            <h2
              className={clsxm(
                agents.display,
                'text-[2.25rem] font-semibold leading-[1.06] text-white sm:text-5xl lg:text-[3.5rem]',
              )}
            >
              {t('Never miss the next call.')}
            </h2>
            <p className='mt-6 max-w-lg text-lg leading-8 text-primary-100'>
              {t(
                'Build and test your agent free. When you’re ready for calls, connect a Twilio number on any paid plan.',
              )}
            </p>
            <div className='mt-10 flex flex-wrap items-center gap-3'>
              <Link
                href={SIGNUP_HREF}
                className='inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3.5 text-[15px] font-semibold text-primary-700 shadow-[0_10px_30px_-10px_rgba(20,10,80,0.6)] transition-colors duration-200 hover:bg-primary-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
              >
                {t('Build your agent free')}
                <ArrowGlyph />
              </Link>
              <button
                type='button'
                onClick={openDemo}
                className='inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-semibold text-white ring-1 ring-inset ring-white/35 transition duration-200 hover:bg-white/10 hover:ring-white/60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white'
              >
                {t('Talk to our agent')}
              </button>
            </div>
          </div>

          <div aria-hidden='true' className='hidden lg:block'>
            <div className={clsxm(agents.float, 'ms-auto max-w-sm')}>
              <div className='rounded-[22px] bg-white p-5 text-[#10101c] shadow-[0_30px_60px_-25px_rgba(20,10,80,0.7)]'>
                <div className='flex items-center gap-3'>
                  <span
                    className={clsxm(
                      'flex h-10 w-10 flex-none items-center justify-center rounded-full text-white',
                      BRAND_BG,
                    )}
                  >
                    <PhoneGlyph className='h-[18px] w-[18px]' />
                  </span>
                  <span className='min-w-0 flex-1'>
                    <span className='block truncate text-[15px] font-semibold'>
                      {BUSINESS}
                    </span>
                    <span className='block text-xs text-[#6f6f86]'>
                      {t('Incoming call')} · {t('11:48 p.m.')}
                    </span>
                  </span>
                </div>
                <span
                  className={clsxm(
                    agents.wave,
                    agents.waveCenter,
                    'mt-4 flex h-8 items-center justify-between',
                  )}
                >
                  {WAVE.map((height, index) => (
                    <span
                      key={index}
                      className={clsxm('w-[4px] rounded-full', BRAND_BG)}
                      style={{
                        height: `${Math.max(4, Math.round(height * 0.7))}px`,
                        opacity: 0.35 + (height / 42) * 0.65,
                        ...delay(index * 55),
                      }}
                    />
                  ))}
                </span>
                <p className='mt-4 flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-[13px] font-semibold text-emerald-800'>
                  <CheckGlyph className='h-4 w-4 text-emerald-600' />
                  {t('Answered by your AI receptionist')}
                </p>
              </div>
              <p className='mt-3 flex items-center gap-2 ps-1 text-xs font-medium text-primary-100'>
                <span className='h-1.5 w-1.5 rounded-full bg-emerald-400' />
                {t('Answered while the office was closed.')}
              </p>
            </div>
          </div>
        </div>
      </Stage>
    </section>
  );
}
